import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsApi } from '../services/api';
import { Notification } from '../types';

export function useNotifications() {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await analyticsApi.getNotifications();
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await analyticsApi.markNotificationRead('all');
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notif: Notification) => {
    if (!notif.is_read) {
      try {
        await analyticsApi.markNotificationRead(notif.id);
        setNotifications(notifications.map(n =>
          n.id === notif.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    setShowNotifications(false);
    const refParam = notif.reference_id ? `?highlight=${notif.reference_id}` : '';
    // Birthday/anniversary announcements route to Celebrations. The scheduler's
    // notification message always says "posted a celebration announcement",
    // which is reliable even when the template title has no keyword in it.
    const haystack = `${notif.title || ''} ${notif.message || ''}`.toLowerCase();
    const isCelebration = haystack.includes('birthday') || haystack.includes('anniversary') || haystack.includes('celebration');

    if (notif.reference_type === 'appreciation' || notif.reference_type === 'appreciation_comment') {
      navigate(`/feed${refParam}`);
    } else if (isCelebration && notif.reference_type !== 'stream' && notif.reference_type !== 'stream_comment') {
      navigate(`/celebrations${refParam}`);
    } else if (notif.reference_type === 'announcement' || notif.reference_type === 'announcement_comment') {
      navigate(`/announcements${refParam}`);
    } else if (notif.reference_type === 'stream' || notif.reference_type === 'stream_comment') {
      navigate(`/streams${refParam}`);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return {
    notifications,
    unreadCount,
    showNotifications,
    setShowNotifications,
    handleMarkAllRead,
    handleNotificationClick,
    formatTime,
    fetchNotifications,
  };
}
